import type { ColDef, ICellRendererParams, IRowNode } from "ag-grid-community"
import { sum } from "lodash"
import { createNumberColumn } from "~/utils/columnTemplates"
import { filterDefined, uniq } from "~/utils/tsUtils"
import { roundNumber } from "~/utils/helpers"
import { GMROIBreakdownHoverCard } from "~/components/reusable/GMROIBreakdownHoverCard"
import { InventoryTurnBreakdownHoverCard } from "~/components/reusable/InventoryTurnBreakdownHoverCard"
import { ProductTableColumn } from "~/constants/productTableColumns"
import type { RowDataType } from "./PurchaseOrderTableV2"

type VariantTurnEntry = {
  cogs?: number | null
  avgInvValue?: number | null
  lookbackStartDate?: Date | string | null
}
type VariantSalesEntry = { lastYearRevenue?: number | null }

type Aggregates = {
  cogs: number
  avgInv: number
  revenue: number
  costPrice: number | null
  startDate: Date | null
}

type Deps = {
  variantInventoryTurnMap: ReadonlyMap<string, VariantTurnEntry> | undefined
  variantSalesSummaries: ReadonlyMap<string, VariantSalesEntry> | undefined
  company: { currency?: string | null } | undefined
}

function getAggregates(
  node: IRowNode<RowDataType> | null | undefined,
  { variantInventoryTurnMap, variantSalesSummaries }: Deps,
): Aggregates {
  if (node?.group) {
    const leaves = (node.allLeafChildren ?? []) as IRowNode<RowDataType>[]
    const variantIds = uniq(filterDefined(leaves.map((n) => n.data?.variantId)))
    const turnEntries = filterDefined(variantIds.map((vid) => variantInventoryTurnMap?.get(vid)))
    const cogs = sum(turnEntries.map((e) => e.cogs ?? 0))
    const avgInv = sum(turnEntries.map((e) => e.avgInvValue ?? 0))
    const revenue = sum(
      variantIds.map((vid) => variantSalesSummaries?.get(vid)?.lastYearRevenue ?? 0),
    )
    // Shown in the hover card for context only — not used in any calculation.
    const costPrice = leaves.reduce<number>((acc, n) => Math.max(acc, n.data?.costPrice ?? 0), 0)
    const dateMs = turnEntries.flatMap((e) =>
      e.lookbackStartDate ? [new Date(e.lookbackStartDate).getTime()] : [],
    )
    const minMs = dateMs.length > 0 ? Math.min(...dateMs) : null
    return { cogs, avgInv, revenue, costPrice, startDate: minMs != null ? new Date(minMs) : null }
  }
  const data = node?.data
  return {
    cogs: data?.inventoryTurnCogs ?? 0,
    avgInv: data?.inventoryTurnAvgInvValue ?? 0,
    revenue: data?.revenue ?? 0,
    costPrice: data?.costPrice ?? null,
    startDate: data?.inventoryTurnStartDate ? new Date(data.inventoryTurnStartDate) : null,
  }
}

const formatRatio = (value: number | null) => (value != null ? String(roundNumber(value, 2)) : "-")

const turnRatio = (cogs: number, avgInv: number) => (avgInv > 0 ? cogs / avgInv : null)
const gmroiRatio = (revenue: number, cogs: number, avgInv: number) =>
  avgInv > 0 ? (revenue - cogs) / avgInv : null

export function inventoryTurnColumns(deps: Deps): ColDef<RowDataType>[] {
  return [
    createNumberColumn<RowDataType>({
      colId: ProductTableColumn.InventoryTurn,
      headerName: "Inventory turn",
      headerSubtitle: "Last 12 months",
      decimals: 2,
      valueGetter: (params) => {
        const { cogs, avgInv } = getAggregates(params.node, deps)
        return turnRatio(cogs, avgInv)
      },
      aggFunc: (params) => {
        const { cogs, avgInv } = getAggregates(params.rowNode, deps)
        return turnRatio(cogs, avgInv)
      },
      cellStyle: { display: "flex", alignItems: "center", overflow: "hidden" },
      cellRenderer: (params: ICellRendererParams<RowDataType, number | null>) => {
        if (params.node?.rowPinned) return <span>{formatRatio(params.value ?? null)}</span>
        const { cogs, avgInv, costPrice, startDate } = getAggregates(params.node, deps)
        return (
          <InventoryTurnBreakdownHoverCard
            cogs={cogs}
            avgInvValue={avgInv}
            costPrice={costPrice}
            currency={deps.company?.currency ?? undefined}
            startDate={startDate}
          >
            <div className="w-full">{formatRatio(turnRatio(cogs, avgInv))}</div>
          </InventoryTurnBreakdownHoverCard>
        )
      },
    }),
    createNumberColumn<RowDataType>({
      colId: ProductTableColumn.GMROI,
      headerName: "GMROI",
      headerSubtitle: "Last 12 months",
      decimals: 2,
      valueGetter: (params) => {
        const { cogs, avgInv, revenue } = getAggregates(params.node, deps)
        return gmroiRatio(revenue, cogs, avgInv)
      },
      aggFunc: (params) => {
        const { cogs, avgInv, revenue } = getAggregates(params.rowNode, deps)
        return gmroiRatio(revenue, cogs, avgInv)
      },
      cellStyle: { display: "flex", alignItems: "center", overflow: "hidden" },
      cellRenderer: (params: ICellRendererParams<RowDataType, number | null>) => {
        if (params.node?.rowPinned) return <span>{formatRatio(params.value ?? null)}</span>
        const { cogs, avgInv, revenue, costPrice, startDate } = getAggregates(params.node, deps)
        return (
          <GMROIBreakdownHoverCard
            revenue={revenue}
            cogs={cogs}
            avgInvValue={avgInv}
            costPrice={costPrice}
            currency={deps.company?.currency ?? undefined}
            startDate={startDate}
          >
            <div className="w-full">{formatRatio(gmroiRatio(revenue, cogs, avgInv))}</div>
          </GMROIBreakdownHoverCard>
        )
      },
    }),
  ]
}
